import { useEffect, useRef } from 'react';

type EffectCleanup = void | (() => void | undefined);

type ValueChangeCallback<V> = (newValue: V, prevValue: V) => EffectCleanup;

type EqualityCheck<V> = (newValue: V, prevValue: V) => boolean;

type Options<V> = {
    /**
     * The effect which is triggered if the observed value has changed.
     */
    readonly effect: ValueChangeCallback<V>;

    /**
     * The value which is being observed for changes.
     */
    readonly observedValue: V;

    /**
     * Skip the first value change
     */
    readonly skipInitial?: boolean;

    /**
     * An equality function which is used for checking the observed value for changes. Defaults to `Object.is`.
     */
    readonly equals?: EqualityCheck<V>;
};

/**
 * An useEffect like hook which is triggered if the given observed value changes.
 */
export function useValueChangeEffect<V>({
    effect,
    observedValue,
    skipInitial = false,
    equals = Object.is
}: Options<V>) {
    const valueRef = useRef(observedValue);
    const valueCounter = useRef(0);

    useEffect(() => {
        if (!equals(valueRef.current, observedValue)) {
            const callCount = valueCounter.current;
            valueCounter.current++;

            if (!skipInitial || callCount > 0) {
                const cleanUp = effect(observedValue, valueRef.current);
                valueRef.current = observedValue;
                return cleanUp;
            }
        }
    }, [observedValue, effect, equals, skipInitial]);
}
